import React, { Children } from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  oneOfType,
  shape,
  string
} from 'prop-types';

import ClassNames from 'classnames';

import BaseComponent from '../.internal/BaseComponent';
import TabButton from './components/TabButton';

import './Tabs.scss';

export default class Tabs extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = { activeTabIndex: 0 };

    this.tabsRef = React.createRef();
  }

  componentDidMount() {
    const { children } = this.props;

    let activeTabIndex = 0;

    Children.toArray(children).forEach((child, index) => {
      if (child.props.default) {
        activeTabIndex = index;
      }
    });

    this.highlightActiveTab(activeTabIndex);

    this.setState({ activeTabIndex });
  }

  renderTabButtons = () => {
    const { children, inner } = this.props;
    const tabs = Children.map(children, (element, index) => {
      const { title, disabled, style } = element.props;
      const { activeTabIndex } = this.state;
      const active = activeTabIndex === index;
      const onTabClick = !active ? this.onTabClick : undefined;

      return (
        <TabButton
          active={active}
          disabled={disabled}
          id={`tab-${index}`}
          inner={inner}
          style={style}
          title={title}
          onClick={() => onTabClick(index)}
        />
      );
    });

    return tabs;
  };

  highlightActiveTab = index => {
    const element = this.tabsRef.current.querySelector(`#tab-${index}`);

    if (!this.props.inner) {
      const clickedTab = element;
      const tabsInkBar = element
        .closest('.tab-buttons-container')
        .querySelector('.tabs-ink-bar');
      const tabLength = clickedTab.offsetWidth;
      const tabOffset = clickedTab.offsetLeft;

      tabsInkBar.setAttribute(
        'style',
        `width: ${tabLength}px; transform: translate3d(${tabOffset}px, 0px, 0px)`
      );
    }
  };

  onTabClick = index => {
    this.highlightActiveTab(index);
    this.setState({ activeTabIndex: index });
  };

  componentDidUpdate(prevProps, prevState) {
    const { activeTabIndex } = this.state;
    const { onChange } = this.props;

    if (activeTabIndex !== prevState.activeTabIndex) {
      this.highlightActiveTab(activeTabIndex);

      if (prevState.activeTabIndex !== null && onChange) {
        onChange();
      }
    }
  }

  render() {
    const { children, style, id, inner, className } = this.props;
    const { activeTabIndex } = this.state;
    let content;

    const classNames = ClassNames(['Tabs', className]);

    const containerClassNames = ClassNames([
      'tab-buttons-container',
      inner && 'tab-inner'
    ]);

    Children.forEach(children, (element, index) => {
      if (index === activeTabIndex) {
        content = element;
      }
    });

    return (
      <div className={classNames} style={style} id={id} ref={this.tabsRef}>
        <div className={containerClassNames}>
          {this.renderTabButtons()}
          {!inner && <div className="tabs-ink-bar" />}
        </div>
        <div className="tab-content-container">{content}</div>
      </div>
    );
  }
}

Tabs.propTypes = {
  children: oneOfType([arrayOf(node), node]).isRequired,
  className: string,
  /**
   * **Deprecated:** Use `default` prop on `<Tab>` component instead
   */
  defaultActive: string,
  id: string,
  inner: bool,
  onChange: func,
  style: shape({})
};

Tabs.defaultProps = {
  children: undefined,
  className: undefined,
  defaultActive: undefined,
  id: undefined,
  inner: false,
  onChange: undefined,
  style: {}
};
